import { Immutable } from "immer";
import React from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    FORM_LABEL_CLASS_NAME,
    FORM_LABEL_DYNAMIC_CLASS_NAME,
    FORM_LABEL_OUTSIDE_CLASS_NAME,
} from "../classNames";

export type LabelPlacement =
    | "top"
    | "left"
    | "right"
    | "bottom"
    | "outside"
    | "dynamic"
    | "static";

/**
 * Per-placement classes. `dynamic` and `outside` have styling of their own in
 * the forms module (the module + literal pairs live in `classNames.ts`); the
 * rest are bare literal hooks.
 */
const PLACEMENT_CLASS_NAMES: Readonly<Record<LabelPlacement, string>> = {
    top: "form__label--top",
    left: "form__label--left",
    right: "form__label--right",
    bottom: "form__label--bottom",
    outside: FORM_LABEL_OUTSIDE_CLASS_NAME,
    dynamic: FORM_LABEL_DYNAMIC_CLASS_NAME,
    static: "form__label--static",
};

/**
 * Renders form field label
 *
 * The literal `form__label*` classes stay alongside the module classes while
 * legacy contextual rules still target them (#34958).
 */
export const FormLabel = (
    props: Immutable<{
        id?: string;
        label?: string;
        placeholder?: string;
        labelCSSClass?: string;
        labelPlacement?: LabelPlacement;
        onLabelClick?: () => void;
    }>,
): React.ReactNode => {
    const text = props.label || props.placeholder;
    if (!text) {
        return null;
    }

    const classes = concatClassNames([
        FORM_LABEL_CLASS_NAME,
        props.labelCSSClass,
        props.labelPlacement
            ? PLACEMENT_CLASS_NAMES[props.labelPlacement]
            : null,
    ]);

    return (
        <label
            className={classes}
            htmlFor={props.id}
            onClick={props.onLabelClick}
        >
            {text}
        </label>
    );
};
